import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import {
  getAuth,
  RecaptchaVerifier,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  getMultiFactorResolver,
  MultiFactorSession,
  MultiFactorInfo,
  MultiFactorResolver,
  PhoneInfoOptions,
  MultiFactorError,
  ParsedToken,
  User,
  OAuthProvider
} from 'firebase/auth';

import { FIREBASE_CONFIG } from '../../env';
import { trackEvent } from '../app/analytics/ga';

export interface Claims extends ParsedToken {
  assessment: { active: boolean };
  metric_collection: { active: boolean; type: string };
  readiness_guage: { active: boolean };
  reports: { active: boolean };
  resource_centre: { active: boolean };
}

// Initialize Firebase
const app = initializeApp(FIREBASE_CONFIG);
export const db = getFirestore(app);

export const auth = getAuth(app);

let verification_id: string | null = null;
let multi_factor_resolver: MultiFactorResolver | null = null;
let recaptcha_verifier: RecaptchaVerifier | null = null;

// Boolean tracker for whether a temporary auth state is being used for account linking.
let is_auth_linking_in_progress = false;

export const setIsAuthLinkingInProgress = (value: boolean): void => {
  is_auth_linking_in_progress = value;
};

export const isAuthLinkingInProgress = (): boolean => is_auth_linking_in_progress;

let sso_expected_email: string | null = null;

export const setSsoExpectedEmail = (email: string | null): void => {
  sso_expected_email = email;
};

export const getSsoExpectedEmail = (): string | null => sso_expected_email;

/**
 * Resets all recaptcha variables to null to ensure clean start of any new recaptchas
 * @returns {void}
 */
export const clearRecaptchaData = (): void => {
  // Remove old container
  const old_container: HTMLElement | null = document.getElementById('recaptcha-container');
  if (old_container) {
    old_container.remove();
  }

  // Create new container with same ID
  const new_container: HTMLDivElement = document.createElement('div');
  new_container.id = 'recaptcha-container';
  document.body.appendChild(new_container);

  // Clear all data regarding old recaptcha
  recaptcha_verifier?.clear();
  verification_id = null;
  multi_factor_resolver = null;
  recaptcha_verifier = null;
};

/**
 * Sets the global verification id
 * @param {string} id Verification id generated by frontend function
 * @returns {RecaptchaVerifier | null}
 */

export const setVerificationId = (id: string) => {
  verification_id = id;
};

/**
 * Generates a new global recaptcha verifier and returns it to caller
 * @returns {RecaptchaVerifier | null}
 */

export const generateRecaptchaVerifier = () => {
  recaptcha_verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
    size: 'invisible'
  });
  return recaptcha_verifier;
};

/**
 * Getter to return the global recaptcha verifier
 * @returns {RecaptchaVerifier | null}
 */

export const getRecaptchaVerifier = () => {
  return recaptcha_verifier;
};

/**
 * Function to facilitate a user submitting their phone number for MFA
 * @param {string | null} phone_number Phone number that user wishes to register to their account
 * @returns {void}
 */

export const startEnrollMultiFactor = async (phone_number: string | null) => {
  try {
    const recaptcha_verifier = new RecaptchaVerifier(auth, 'sign-in-button', { size: 'invisible' });

    if (auth.currentUser) {
      verification_id = await multiFactor(auth.currentUser)
        .getSession()
        .then(function (multiFactorSession) {
          // Specify the phone number and pass the MFA session.
          const phone_info_options = {
            phone_number: phone_number,
            session: multiFactorSession
          };

          const phone_auth_provider = new PhoneAuthProvider(auth);

          // Send SMS verification code.
          return phone_auth_provider.verifyPhoneNumber(phone_info_options, recaptcha_verifier);
        })
        .catch(function (error) {
          throw error;
        });
    } else {
      // The user is not verified.
      return;
    }
  } catch (err) {
    return err;
  }
};

/**
 * Function to facilitate verification of newly registered MFA phone number
 * @param {string} verification_code verification code sent through during startEnrollMulitfactor
 * @returns {void}
 */

export const finishEnrollMultiFactor = async (verification_code: string) => {
  if (verification_id && auth.currentUser) {
    try {
      const cred = PhoneAuthProvider.credential(verification_id, verification_code);
      const multi_factor_assertioin = PhoneMultiFactorGenerator.assertion(cred);

      // Complete enrollment.
      await multiFactor(auth.currentUser)
        .enroll(multi_factor_assertioin, 'My cellphone number')
        .catch(function (error) {
          throw error;
        });
      verification_id = null;
    } catch (err) {
      return err;
    }
  }
};

/**
 * Function to facilitate MFA login process
 * @param {MultiFactorError} error Firebase error allowing the trigger of MFA Resolver generation
 * @returns {MultiFactorResolver} Generated MFA Resolved that allows MFA login to occur
 */

export const generateMfaResolver = (error: MultiFactorError) => {
  multi_factor_resolver = getMultiFactorResolver(auth, error);
  return multi_factor_resolver;
};

/**
 * Function to retrieve global multifactor resolver
 * @returns {MultiFactorResolver | null} Generated MFA Resolved that allows MFA login to occur
 */

export const retrieveMultiFactorResolver = () => {
  return multi_factor_resolver;
};

/**
 * Function to facilitate the start of sign in in with MFA
 * @param {MultiFactorInfo} error A structure containing the information of a second factor entity.
 * @param {MultiFactorSession} session An interface defining the multi-factor session object used for helping sign in an enrolled user with a second factor.
 * @returns {void}
 */

export const startSmsMfaSignin = async (
  multi_factor_hint: MultiFactorInfo,
  session: MultiFactorSession,
  recaptcha_verifier: RecaptchaVerifier
) => {
  try {
    // const recaptcha_verifier = new RecaptchaVerifier(auth, 'sign-in-button', { size: 'invisible' });
    if (multi_factor_hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
      const phone_info_options: PhoneInfoOptions = {
        multiFactorHint: multi_factor_hint,
        session: session
      };
      const phone_auth_provider = new PhoneAuthProvider(auth);
      // Send SMS verification code
      verification_id = await phone_auth_provider
        .verifyPhoneNumber(phone_info_options, recaptcha_verifier)
        .catch(function (error) {
          throw new Error(error);
        });
    } else {
      return;
    }
  } catch (err: unknown) {
    throw new Error(
      err instanceof Error
        ? err.message
        : 'Error: lib/google/firebase.ts failed on an unknown error while calling startMfaSignin.'
    );
  }
};

/**
 * Function to facilitate the verification of sign in in with MFA
 * @param {string} verification_code Verification code sent through during startMfaSignIn
 * @returns {void}
 */

export const finishMfaSignIn = async (verification_code: string) => {
  // Get the SMS verification code sent to the user.
  try {
    if (verification_id && multi_factor_resolver) {
      const cred = PhoneAuthProvider.credential(verification_id, verification_code);
      const multi_factor_assertion = PhoneMultiFactorGenerator.assertion(cred);

      // Complete sign-in.
      await multi_factor_resolver
        .resolveSignIn(multi_factor_assertion)
        .then(function (user_credential) {
          // Send user login event to Google Analytics.
          trackEvent(
            'user_login',
            {
              syntiro_user_id: auth.currentUser?.uid,
              syntiro_user_email: auth.currentUser?.email
            },
            false
          );
          return user_credential;
        });
    }
  } catch (err: unknown) {
    if (err instanceof Error && 'code' in err) {
      throw new FirebaseError((err as FirebaseError).code, err.message);
    } else {
      throw new Error(
        err instanceof Error
          ? err.message
          : 'Error: lib/google/firebase.ts failed on an unknown error while calling finishMfaSignIn.'
      );
    }
  }
};

/**
 * Function to obtain the claims object assigned to a user through Firebase Auth
 * @param {User | null} user User record object returned throuh Firebase JWT token auth access
 * @returns {void}
 */

export const getUserClaims = async () => {
  return new Promise<Claims>((resolve, reject) => {
    try {
      auth.onAuthStateChanged((user: User | null) => {
        if (user) {
          user
            .getIdTokenResult()
            .then((idTokenResult) => {
              resolve(idTokenResult.claims as Claims);
            })
            .catch((err: unknown) => {
              reject(err);
            });
        }
      });
    } catch (err: unknown) {
      throw new Error(
        err instanceof Error
          ? err.message
          : 'Error: lib/google/firebase.ts failed on an unknown error while calling getUserClaims.'
      );
    }
  }).catch((error) => {
    throw new Error(error);
  });
};

export interface MicrosoftProviderOptions {
  /** Pre-selects a known account so the user is not shown an unrelated cached session. */
  login_hint?: string;
  /** Forces the Microsoft account picker instead of silently reusing the active session. */
  force_account_picker?: boolean;
}

/**
 * Builds the Microsoft SSO provider, optionally forcing the Microsoft account picker.
 * Without a prompt Microsoft silently reuses the browser's active work account, so
 * `force_account_picker` is required to let a user sign in as a different account.
 * @param {MicrosoftProviderOptions} options Controls the Microsoft account selection experience.
 * @returns {OAuthProvider}
 */
export const createMicrosoftProvider = (options: MicrosoftProviderOptions = {}): OAuthProvider => {
  const provider = new OAuthProvider('microsoft.com');
  const custom_parameters: Record<string, string> = {
    tenant: 'organizations'
  };
  if (options.force_account_picker) {
    // Interrupts silent SSO and lists every session account plus "Use another account".
    custom_parameters.prompt = 'select_account';
  } else if (options.login_hint) {
    custom_parameters.login_hint = options.login_hint;
  }
  provider.setCustomParameters(custom_parameters);
  return provider;
};

export default app;
